import React, { useState } from 'react'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material'
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  MonetizationOn as MoneyIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'

import api from '../../services/api'
import { formatCurrency, formatRank } from '../../utils/formatNumber'
import { extractErrorMessage } from '../../utils/errorHandler'
import { formatDateJST } from '../../utils/dateUtils'
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel'

interface RankCost {
  rank: string
  monthly_cost: number
  annual_cost: number
  description: string
  is_active: boolean
  created_by: string
  updated_by: string
  created_at: string
  updated_at: string
}

interface RankCostFormData {
  rank: string
  monthly_cost: number
  description: string
}

const RankCostManagementPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRankCost, setEditingRankCost] = useState<RankCost | null>(null)
  const [isCreateMode, setIsCreateMode] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<RankCostFormData>({
    defaultValues: {
      rank: 'G',
      monthly_cost: 0,
      description: '',
    },
  })

  const monthlyCost = watch('monthly_cost')

  // Fetch rank costs
  const { data: rankCostsData, isLoading, refetch } = useQuery({
    queryKey: ['rank-costs'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: RankCost[] }>('/rank-cost')
      return response.data.data
    },
  })

  // Update rank cost mutation
  const updateRankCostMutation = useMutation({
    mutationFn: async (data: RankCostFormData) => {
      const annualCost = data.monthly_cost * 12
      return await api.put(`/rank-cost/${data.rank}`, {
        rank: data.rank,
        monthly_cost: data.monthly_cost,
        annual_cost: annualCost,
        description: data.description,
      })
    },
    onSuccess: () => {
      toast.success('ランクコストを更新しました')
      queryClient.invalidateQueries({ queryKey: ['rank-costs'] })
      setDialogOpen(false)
      setEditingRankCost(null)
      reset()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'ランクコストの更新に失敗しました'))
    },
  })

  // Create rank cost mutation
  const createRankCostMutation = useMutation({
    mutationFn: async (data: RankCostFormData) => {
      const annualCost = data.monthly_cost * 12
      return await api.post('/rank-cost', {
        rank: data.rank.toUpperCase(),
        monthly_cost: data.monthly_cost,
        annual_cost: annualCost,
        description: data.description,
      })
    },
    onSuccess: () => {
      toast.success('新規ランクコストを作成しました')
      queryClient.invalidateQueries({ queryKey: ['rank-costs'] })
      setDialogOpen(false)
      setIsCreateMode(false)
      reset()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'ランクコストの作成に失敗しました'))
    },
  })

  // Delete rank cost mutation
  const deleteRankCostMutation = useMutation({
    mutationFn: async (rank: string) => {
      return await api.delete(`/rank-cost/${rank}`)
    },
    onSuccess: () => {
      toast.success('ランクコストを削除しました')
      queryClient.invalidateQueries({ queryKey: ['rank-costs'] })
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'ランクコストの削除に失敗しました'))
    },
  })

  const handleEditRankCost = (rankCost: RankCost) => {
    setEditingRankCost(rankCost)
    setIsCreateMode(false)
    reset({
      rank: rankCost.rank,
      monthly_cost: rankCost.monthly_cost,
      description: rankCost.description || '',
    })
    setDialogOpen(true)
  }

  const handleCreateRankCost = () => {
    setEditingRankCost(null)
    setIsCreateMode(true)
    reset({
      rank: '',
      monthly_cost: 0,
      description: '',
    })
    setDialogOpen(true)
  }

  const handleDeleteRankCost = async (rank: string) => {
    if (window.confirm(`ランク「${rank}」を削除しますか？この操作は取り消せません。`)) {
      await deleteRankCostMutation.mutateAsync(rank)
    }
  }

  const onSubmit = async (data: RankCostFormData) => {
    if (isCreateMode) {
      await createRankCostMutation.mutateAsync(data)
    } else {
      await updateRankCostMutation.mutateAsync(data)
    }
  }

  const rankCosts = rankCostsData || []

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MoneyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            ランクコスト管理
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            更新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateRankCost}
          >
            新規
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          <strong>ランクコストシステム:</strong> 各等級の年額ランクコストを管理します。
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ※ 年額コストは月額コスト × 12で自動計算されます。
        </Typography>
      </Alert>

      {isLoading ? (
        <Typography>読み込み中...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ランク</TableCell>
                <TableCell>説明</TableCell>
                <TableCell align="right">月額コスト</TableCell>
                <TableCell align="right">年額コスト</TableCell>
                <TableCell align="center">更新者</TableCell>
                <TableCell align="center">更新日時</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rankCosts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    ランクコストデータがありません
                  </TableCell>
                </TableRow>
              ) : (
                rankCosts.map((rankCost) => (
                  <TableRow key={rankCost.rank}>
                    <TableCell>
                      <Chip
                        label={rankCost.rank}
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {rankCost.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(rankCost.monthly_cost)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="primary.main">
                        {formatCurrency(rankCost.annual_cost)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">{rankCost.updated_by}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">
                        {formatDateJST(rankCost.updated_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="編集">
                        <IconButton
                          size="small"
                          onClick={() => handleEditRankCost(rankCost)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteRankCost(rankCost.rank)}
                          disabled={deleteRankCostMutation.isPending}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {isCreateMode ? '新規ランクコスト作成' : 'ランクコスト編集'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Alert severity="warning" icon={<InfoIcon />}>
                <Typography variant="caption">
                  {isCreateMode
                    ? 'ランクは大文字アルファベット1文字（A-Z）で指定してください。'
                    : 'このランクコストは全社員の評価計算に影響します。変更は慎重に行ってください。'}
                </Typography>
              </Alert>

              <Controller
                name="rank"
                control={control}
                rules={{
                  required: 'ランクは必須です',
                  pattern: {
                    value: /^[A-Za-z]$/,
                    message: 'ランクはアルファベット1文字で指定してください',
                  },
                }}
                render={({ field }) => (
                  <Box>
                    <RequiredFieldLabel label="ランク" />
                    <TextField
                      {...field}
                      fullWidth
                      disabled={!isCreateMode}
                      error={!!errors.rank}
                      helperText={errors.rank?.message || (isCreateMode ? '例: J, K, L など' : '')}
                      inputProps={{ maxLength: 1, style: { textTransform: 'uppercase' } }}
                      InputProps={{
                        startAdornment: !isCreateMode && field.value ? (
                          <InputAdornment position="start">
                            <Chip label={field.value} color="primary" size="small" />
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Box>
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="説明"
                    multiline
                    rows={2}
                    placeholder="例: 上位等級 - 部長クラス"
                  />
                )}
              />

              <Controller
                name="monthly_cost"
                control={control}
                rules={{
                  required: '月額コストは必須です',
                  min: { value: 0, message: '月額コストは0以上である必要があります' },
                }}
                render={({ field }) => (
                  <Box>
                    <RequiredFieldLabel label="月額コスト" />
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      error={!!errors.monthly_cost}
                      helperText={errors.monthly_cost?.message}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                      }}
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    />
                  </Box>
                )}
              />

              <TextField
                label="年額コスト（自動計算）"
                type="text"
                disabled
                value={formatCurrency(monthlyCost * 12)}
                helperText="月額コスト × 12で自動計算されます"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCreateMode ? createRankCostMutation.isPending : updateRankCostMutation.isPending}
            >
              {isCreateMode ? '作成' : '更新'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  )
}

export default RankCostManagementPage
